import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Heart, Target, Globe, Banknote, Sparkles, 
  Briefcase, Lightbulb, MapPin, Loader2, RefreshCw, Download, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import IkigaiDiagram from '../components/IkigaiDiagram';
import SocialShare from '../components/SocialShare';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Results = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef(null);

  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analysis/profile`);
      if (response.data && response.data.analysis) {
        setProfile(response.data);
      } else {
        setError(language === 'fr' ? 'Données de profil incomplètes' : 'Incomplete profile data');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        toast.info(language === 'fr' ? 'Aucun profil trouvé. Complète les modules d\'abord.' : 'No profile found. Complete the modules first.');
        navigate('/dashboard');
      } else if (err.response?.status === 402) {
        toast.error(language === 'fr' ? 'Achetez le diagnostic pour accéder aux résultats' : 'Purchase the diagnostic to access results');
        navigate('/payment');
      } else {
        const msg = err.response?.data?.detail || err.message || 'Unknown error';
        setError(language === 'fr' ? `Erreur de chargement: ${msg}` : `Loading error: ${msg}`);
      }
    } finally {
      setLoading(false);
    }
  }, [language, navigate]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const response = await axios.post(`${API}/analysis/generate`, {}, { timeout: 120000 });
      setProfile({ analysis: response.data.analysis });
      toast.success(language === 'fr' ? 'Profil régénéré !' : 'Profile regenerated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setRegenerating(false);
    }
  };

  const handleExportPDF = async () => {
    if (!reportRef.current) return;
    
    setExporting(true);
    toast.info(language === 'fr' ? 'Génération du PDF en cours...' : 'Generating PDF...');

    // Get analysis data
    const analysisData = profile?.analysis;
    if (!analysisData) {
      toast.error(language === 'fr' ? 'Aucune analyse disponible' : 'No analysis available');
      setExporting(false);
      return;
    }

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = margin;

      // Header with gradient effect
      pdf.setFillColor(255, 107, 107);
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Title
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Mon Profil Ikigai', pageWidth / 2, 18, { align: 'center' });
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`${user?.name || 'Utilisateur'} - ${new Date().toLocaleDateString('fr-FR')}`, pageWidth / 2, 30, { align: 'center' });

      yPosition = 50;

      // Ikigai Summary
      if (analysisData?.ikigai_summary) {
        pdf.setTextColor(50, 50, 50);
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'italic');
        const summaryLines = pdf.splitTextToSize(`"${analysisData.ikigai_summary}"`, pageWidth - 2 * margin);
        pdf.text(summaryLines, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += summaryLines.length * 6 + 10;
      }

      // Helper function to add a section
      const addSection = (title, items, color, icon) => {
        if (!items || items.length === 0) return;

        // Check if we need a new page
        if (yPosition > pageHeight - 60) {
          pdf.addPage();
          yPosition = margin;
        }

        // Section header with colored bar
        pdf.setFillColor(...color);
        pdf.rect(margin, yPosition, 4, 8, 'F');
        
        pdf.setTextColor(50, 50, 50);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(title, margin + 8, yPosition + 6);
        yPosition += 12;

        // Items
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        items.forEach((item, index) => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }
          
          const itemText = typeof item === 'string' ? item : item.title || item.name || item.description || JSON.stringify(item);
          
          // Bullet point
          pdf.setFillColor(...color);
          pdf.circle(margin + 3, yPosition - 1, 1.5, 'F');
          
          // Text
          pdf.setTextColor(80, 80, 80);
          const lines = pdf.splitTextToSize(itemText, pageWidth - 2 * margin - 10);
          pdf.text(lines, margin + 8, yPosition);
          yPosition += lines.length * 5 + 3;
        });

        yPosition += 8;
      };

      // Personality Profile
      if (analysisData?.personality_profile) {
        pdf.setFillColor(255, 107, 107);
        pdf.rect(margin, yPosition, 4, 8, 'F');
        
        pdf.setTextColor(50, 50, 50);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(language === 'fr' ? 'Profil de Personnalité' : 'Personality Profile', margin + 8, yPosition + 6);
        yPosition += 14;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(80, 80, 80);
        const profileLines = pdf.splitTextToSize(analysisData.personality_profile, pageWidth - 2 * margin);
        pdf.text(profileLines, margin, yPosition);
        yPosition += profileLines.length * 5 + 12;
      }

      // Add sections
      addSection(
        language === 'fr' ? 'Passions' : 'Passions',
        analysisData?.core_passions,
        [255, 107, 107]
      );

      addSection(
        language === 'fr' ? 'Talents Naturels' : 'Natural Talents',
        analysisData?.natural_talents,
        [255, 230, 109]
      );

      addSection(
        language === 'fr' ? 'Causes Importantes' : 'Important Causes',
        analysisData?.causes,
        [78, 205, 196]
      );

      addSection(
        language === 'fr' ? 'Environnements Idéaux' : 'Ideal Environments',
        analysisData?.ideal_environments,
        [26, 83, 92]
      );

      addSection(
        language === 'fr' ? 'Pistes de Carrière' : 'Career Paths',
        analysisData?.career_paths,
        [255, 107, 107]
      );

      addSection(
        language === 'fr' ? 'Projets Concrets' : 'Concrete Projects',
        analysisData?.concrete_projects,
        [78, 205, 196]
      );

      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(
        `Généré par Ikigai App - ${new Date().toLocaleDateString('fr-FR')}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );

      // Save
      pdf.save(`ikigai-profile-${user?.name || 'user'}.pdf`);
      toast.success(language === 'fr' ? 'PDF téléchargé !' : 'PDF downloaded!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de l\'export' : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="flex items-center gap-3 text-stone-600">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>{t('loading')}</span>
        </div>
      </div>
    );
  }

  const analysis = profile?.analysis;

  // Ensure all array fields are actually arrays (AI might return strings or objects)
  const safeArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.map(item => {
      if (typeof item === 'string') return item;
      if (typeof item === 'object' && item !== null) return item.title || item.name || item.description || JSON.stringify(item);
      return String(item);
    });
    if (typeof val === 'string') return val.split(',').map(s => s.trim()).filter(Boolean);
    return [];
  };

  // Protection against missing data
  if (!profile || !analysis || error) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-4">
        <div className="text-center">
          <Sparkles className="w-12 h-12 mx-auto mb-4 text-[#FF6B6B]" />
          <h2 className="font-serif text-xl font-semibold text-stone-900 mb-2">
            {error || (language === 'fr' ? 'Aucun résultat disponible' : 'No results available')}
          </h2>
          <p className="text-stone-500 mb-4">
            {language === 'fr' 
              ? 'Essaie de recharger la page ou retourne au tableau de bord.'
              : 'Try reloading the page or go back to dashboard.'}
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => window.location.reload()} variant="outline" className="rounded-xl">
              {language === 'fr' ? 'Recharger' : 'Reload'}
            </Button>
            <Button onClick={() => navigate('/dashboard')} className="btn-primary">
              {language === 'fr' ? 'Retour au dashboard' : 'Back to dashboard'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors"
            data-testid="back-to-dashboard-results"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Dashboard</span>
          </button>
          
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#FF6B6B]" />
            <span className="font-serif font-semibold text-stone-800">{t('yourIkigai')}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <SocialShare
              title={language === 'fr' ? `J'ai découvert mon Ikigai !` : `I discovered my Ikigai!`}
              description={analysis?.ikigai_summary || (language === 'fr' ? 'Découvre ta vocation avec Ikigai App' : 'Discover your calling with Ikigai App')}
              language={language}
            />
            <Button
              onClick={handleExportPDF}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              disabled={exporting}
              data-testid="export-pdf-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">PDF</span>
            </Button>
            <Button
              onClick={handleRegenerate}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              disabled={regenerating}
              data-testid="regenerate-btn"
            >
              <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{language === 'fr' ? 'Régénérer' : 'Regenerate'}</span>
            </Button>
          </div>
        </div>
      </header>

      <main ref={reportRef} className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#FF6B6B]/10 text-[#FF6B6B] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            {language === 'fr' ? 'Ton profil Ikigai' : 'Your Ikigai Profile'}
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-stone-900 mb-4">
            {user?.name}, {language === 'fr' ? 'voici ton Ikigai' : 'here is your Ikigai'}
          </h1>
          {analysis?.ikigai_summary && (
            <p className="text-lg sm:text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed">
              "{analysis.ikigai_summary}"
            </p>
          )}
        </div>

        {/* Interactive Ikigai Diagram */}
        <div className="card-ikigai p-6 sm:p-8 mb-12">
          <h2 className="font-serif text-xl sm:text-2xl font-semibold text-stone-900 mb-6 text-center">
            {language === 'fr' ? 'Ton diagramme Ikigai' : 'Your Ikigai Diagram'}
          </h2>
          <IkigaiDiagram analysis={analysis} language={language} size="large" />
          <p className="text-center text-sm text-stone-500 mt-6">
            {language === 'fr' 
              ? 'Survole les cercles pour voir tes forces dans chaque domaine'
              : 'Hover over circles to see your strengths in each area'}
          </p>
        </div>

        {/* Personality Profile */}
        {analysis?.personality_profile && (
          <div className="card-ikigai p-6 sm:p-8 mb-8">
            <h2 className="font-serif text-xl sm:text-2xl font-semibold text-stone-900 mb-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF6B6B]/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-[#FF6B6B]" />
              </div>
              {t('personalityProfile')}
            </h2>
            <p className="text-stone-600 text-base sm:text-lg leading-relaxed">{analysis.personality_profile}</p>
          </div>
        )}

        {/* Grid of insights */}
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 mb-12">
          {/* Passions */}
          {analysis?.core_passions && safeArray(analysis.core_passions).length > 0 && (
            <div className="card-ikigai">
              <h3 className="font-serif text-lg sm:text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#FF6B6B]" />
                {t('passions')}
              </h3>
              <ul className="space-y-3">
                {safeArray(analysis.core_passions).map((passion, i) => (
                  <li key={`item-${i}-${typeof item === "string" ? item.slice(0,10) : i}`} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#FF6B6B] mt-2 flex-shrink-0" />
                    <span className="text-stone-600">{passion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Talents */}
          {analysis?.natural_talents && safeArray(analysis.natural_talents).length > 0 && (
            <div className="card-ikigai">
              <h3 className="font-serif text-lg sm:text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-[#FFE66D]" />
                {t('talents')}
              </h3>
              <ul className="space-y-3">
                {safeArray(analysis.natural_talents).map((talent, i) => (
                  <li key={`item-${i}-${typeof item === "string" ? item.slice(0,10) : i}`} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#FFE66D] mt-2 flex-shrink-0" />
                    <span className="text-stone-600">{talent}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Causes */}
          {analysis?.causes && safeArray(analysis.causes).length > 0 && (
            <div className="card-ikigai">
              <h3 className="font-serif text-lg sm:text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#4ECDC4]" />
                {t('causes')}
              </h3>
              <ul className="space-y-3">
                {safeArray(analysis.causes).map((cause, i) => (
                  <li key={`item-${i}-${typeof item === "string" ? item.slice(0,10) : i}`} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#4ECDC4] mt-2 flex-shrink-0" />
                    <span className="text-stone-600">{cause}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Environments */}
          {analysis?.ideal_environments && safeArray(analysis.ideal_environments).length > 0 && (
            <div className="card-ikigai">
              <h3 className="font-serif text-lg sm:text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#1A535C]" />
                {t('idealEnvironments')}
              </h3>
              <ul className="space-y-3">
                {safeArray(analysis.ideal_environments).map((env, i) => (
                  <li key={`item-${i}-${typeof item === "string" ? item.slice(0,10) : i}`} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#1A535C] mt-2 flex-shrink-0" />
                    <span className="text-stone-600">{env}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Career Paths */}
        {analysis?.career_paths && safeArray(analysis.career_paths).length > 0 && (
          <div className="card-ikigai p-6 sm:p-8 mb-8">
            <h2 className="font-serif text-xl sm:text-2xl font-semibold text-stone-900 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#4ECDC4] flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              {t('careerPaths')}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {safeArray(analysis.career_paths).map((career, i) => (
                <div
                  key={`item-${i}-${typeof item === "string" ? item.slice(0,10) : i}`}
                  className="p-4 rounded-2xl bg-gradient-to-br from-stone-50 to-stone-100 border border-stone-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 rounded-full bg-[#FF6B6B]/20 flex items-center justify-center text-xs font-bold text-[#FF6B6B]">
                      {i + 1}
                    </span>
                    <span className="font-semibold text-stone-800">
                      {typeof career === 'string' ? career : career.title || career.name || career}
                    </span>
                  </div>
                  {typeof career === 'object' && career.description && (
                    <p className="text-sm text-stone-600 ml-8">{career.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Concrete Projects */}
        {analysis?.concrete_projects && safeArray(analysis.concrete_projects).length > 0 && (
          <div className="card-ikigai p-6 sm:p-8 bg-gradient-to-br from-[#FF6B6B]/5 to-[#4ECDC4]/5">
            <h2 className="font-serif text-xl sm:text-2xl font-semibold text-stone-900 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FFE66D]/20 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-[#FFE66D]" />
              </div>
              {t('concreteProjects')}
            </h2>
            <div className="space-y-4">
              {safeArray(analysis.concrete_projects).map((project, i) => (
                <div
                  key={`item-${i}-${typeof item === "string" ? item.slice(0,10) : i}`}
                  className="flex items-start gap-4 p-4 bg-white rounded-2xl border border-stone-100 hover:shadow-md transition-shadow"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FFE66D] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {i + 1}
                  </div>
                  <p className="text-stone-700 flex-1">
                    {typeof project === 'string' ? project : project.title || project.description || project}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
          <Button
            onClick={handleExportPDF}
            className="bg-[#4ECDC4] hover:bg-[#3DBDB5] text-white rounded-xl h-12 px-6"
            disabled={exporting}
            data-testid="export-pdf-bottom-btn"
          >
            {exporting ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <FileText className="w-5 h-5 mr-2" />
            )}
            {language === 'fr' ? 'Télécharger mon rapport PDF' : 'Download my PDF report'}
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            className="btn-secondary"
            data-testid="back-dashboard-btn"
          >
            {language === 'fr' ? 'Retour au tableau de bord' : 'Back to dashboard'}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default Results;
